// ** React Imports
import { createContext, useState, ReactNode, useEffect } from 'react'

// ** Next Import
import { useRouter } from 'next/router'

// ** Types
import { AuthValuesType, UserDataType } from './types'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../store'
import { logout } from '../store/apps/resto365UserManagement'
import { clearSession, sessionCountry } from '../@core/lib/session'
import authConfig from 'src/configs/auth'
import { disconnectWebSocket } from '../@core/lib/wsClient'
import { clearReducerOnLogout } from '../store/apps/gyg365Resto'
import { resetFeatureExperience } from '../store/apps/featureExperience'
import { setDatadogUser } from '../observability/datadog'

// ** Defaults
const defaultProvider: AuthValuesType = {
  user: null,
  loading: false,
  setUser: () => null,
  setLoading: () => Boolean,
  handleLogin: () => Promise.resolve(),
  country: sessionCountry,
  logout: () => Promise.resolve()
}

const AuthContext = createContext(defaultProvider)

type Props = {
  children: ReactNode
}

// Workaround to clear the user data from the context.
// TODO: This could be removed when user state is moved to the store.
let performLogout: () => {}

/**
 * Returns the path only if it is a safe relative path (prevents open redirect).
 * Rejects: absolute URLs (http/https), protocol-relative (//), and dangerous schemes.
 */
function getSafeRedirectPath(value: string | null | undefined): string | null {
  if (typeof value !== 'string' || value.length === 0) return null
  const trimmed = value.trim()
  if (trimmed !== value) return null
  if (!trimmed.startsWith('/')) return null
  if (trimmed.startsWith('//')) return null
  if (trimmed.includes('://')) return null

  return trimmed
}

const AuthProvider = ({ children }: Props) => {
  // ** States
  const [user, setUser] = useState<UserDataType | null>(defaultProvider.user)
  const [loading, setLoading] = useState<boolean>(defaultProvider.loading)
  const dispatch = useDispatch<AppDispatch>()

  // ** Hooks
  const router = useRouter()

  const handleLogin = async (userData: UserDataType) => {
    setUser({ ...userData })

    // Set RUM user identity for correlation
    setDatadogUser({ id: userData.id, email: userData.email, name: userData.fullName })

    // Redirect: explicit redirectUri, then returnUrl (query or stored before Okta), then home.
    // All candidates are validated through getSafeRedirectPath to prevent open redirect.
    const storedReturnUrl = typeof window !== 'undefined' ? sessionStorage.getItem('auth.returnUrl') : null
    if (typeof window !== 'undefined' && storedReturnUrl) {
      sessionStorage.removeItem('auth.returnUrl')
    }
    const redirectTo =
      getSafeRedirectPath(userData.redirectUri) ??
      getSafeRedirectPath(router.query.returnUrl as string) ??
      getSafeRedirectPath(storedReturnUrl) ??
      '/'

    // redirectTo is safe: only relative paths from getSafeRedirectPath() or '/'
    await router.replace(redirectTo)
  }

  useEffect(() => {
    const handleStorageEvent = async (event: StorageEvent) => {
      if (event.key === authConfig.storageTokenKeyName) {
        // oktaAuth.revokeAccessToken sets the token to '{}'. handle this case
        if (event.newValue == null || event.newValue === '{}') {
          await handleLogout()
        } else {
          window.location.reload()
        }
      }
    }

    window.addEventListener('storage', handleStorageEvent)

    return () => {
      window.removeEventListener('storage', handleStorageEvent)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleLogout = async () => {
    // Clear RUM user on logout
    setDatadogUser(null)
    await dispatch(clearReducerOnLogout())
    dispatch(resetFeatureExperience())
    setUser(null)
    dispatch(logout())
    disconnectWebSocket()
    await clearSession()
    await router.push('/login')
  }

  const values: AuthValuesType = {
    user,
    loading,
    setUser,
    setLoading,
    handleLogin: handleLogin,
    country: sessionCountry,
    logout: handleLogout
  }

  performLogout = handleLogout

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>
}

export { AuthContext, AuthProvider, performLogout }
